import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme } from '@datatype/type';
import TextBlock from '@component/common/block/text-block';
import TextButton from '@design-system/text-button';
import Gap from '@component/common/gap';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as speakingActions } from '@store/slice/speaking-slice';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import { useSpeakingTasksQuery } from '@hook/iyagigo/useApi';

// ###################################
// AI 말하기 주제(stage) 선택 (생활회화 group-select 모달과 동일 패턴)
// - speaking_task API의 stageKey를 주제 목록으로 묶는다.
// ###################################

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: ${size.MEDIUM_MODAL_WIDTH};
`;

const OptionContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 5px;
  width: 100%;
`;

const OptionItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 12px;
  border-radius: ${size.BORDER_RADIUS_2};
  border: ${size.BORDER_WIDTH_1} solid ${({ $selected }) => ($selected ? color.BROWN_1 : color.GRAY_BOX_BORDER_COLOR)};
  cursor: pointer;
`;

const SpeakingStageSelectModal: React.FC = () => {
  const dispatch = useTypedDispatch();
  const storedStageKey =
    useTypedSelector((state) => state.rootReducer?.speakingReducer?.currentSpeakingStageKey) ?? null;
  const { data: tasksResponse } = useSpeakingTasksQuery(EApiLanguageCode.JAPANESE);
  const tasks = tasksResponse?.data ?? [];
  const stages = tasks.reduce<{ key: string; name: string }[]>((result, task) => {
    if (result.some((stage) => stage.key === task.stageKey)) return result;
    result.push({ key: task.stageKey, name: task.stageName?.ko ?? task.stageKey });
    return result;
  }, []);
  const currentStageKey = stages.some((stage) => stage.key === storedStageKey) ? storedStageKey : stages[0]?.key;
  const [selectedKey, setSelectedKey] = useState<string>(currentStageKey ?? '');

  useEffect(() => {
    if (!selectedKey && currentStageKey) setSelectedKey(currentStageKey);
  }, [currentStageKey, selectedKey]);

  const handleConfirm = () => {
    if (!selectedKey) return;
    dispatch(speakingActions.storeCurrentSpeakingStageKey(selectedKey));
    dispatch(modalActions.hideModal());
  };

  const StageOptions = stages.map((stage) => (
    <OptionItem key={stage.key} $selected={stage.key === selectedKey} onClick={() => setSelectedKey(stage.key)}>
      <TextBlock
        text={stage.name}
        design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'left' } }}
      />
    </OptionItem>
  ));

  return (
    <Wrapper>
      <TextBlock
        text='학습할 주제를 선택해주세요'
        design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'center' } }}
      />
      <Gap height={size.MARGIN_1} />
      <OptionContainer>{StageOptions}</OptionContainer>
      <Gap height={size.MARGIN_2} />
      <TextButton
        text='학습하기'
        theme={ETheme.BROWN}
        size='medium'
        shape='pill'
        width='100%'
        handleClick={handleConfirm}
      />
    </Wrapper>
  );
};

export default SpeakingStageSelectModal;
